import { useState } from "react";
import { ApiKey } from "../../redux/reducers/userSlice";
import { Text, View, Spinner, SettingsList, SettingsListItem } from "../base";
import { Icon } from "../base/Icon";
import { IconButton } from "../base/IconButton";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { ConfirmDialog } from "../ConfirmDialog";

type ApiKeyListProps = {
  apiKeys: ApiKey[];
  onCreateKey: () => void;
  onDeleteKey: (keyId: string) => void;
  className?: string;
  isLoading?: boolean;
};

export const ApiKeyList = ({ apiKeys, onCreateKey, onDeleteKey, className, isLoading }: ApiKeyListProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingKey, setDeletingKey] = useState<ApiKey | null>(null);

  const handleDeleteClick = (key: ApiKey) => {
    setDeletingKey(key);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (deletingKey) {
      onDeleteKey(deletingKey.id);
      setShowDeleteDialog(false);
      setDeletingKey(null);
    }
  };

  return (
    <OrganizationSettingsTab
      title="API Keys"
      count={apiKeys.length}
      className={className}
    >
      {isLoading ? (
        <View className="flex items-center justify-center py-4">
          <Spinner className="!text-brand" />
        </View>
      ) : (
        <SettingsList>
          {apiKeys.map((key) => (
            <SettingsListItem
              key={key.id}
              title={key.description || "Unnamed Key"}
              subtitle={`${key.lastUsedAt ? `Last used ${new Date(key.lastUsedAt).toLocaleDateString()} · ` : ""}Created ${new Date(key.createdAt).toLocaleDateString()}`}
              icon="key"
            >
              <IconButton
                variant="danger"
                icon="trash"
                onClick={() => handleDeleteClick(key)}
              />
            </SettingsListItem>
          ))}
          <button
            onClick={onCreateKey}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add API key
            </Text>
          </button>
        </SettingsList>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingKey(null);
        }}
        onConfirm={confirmDelete}
        title="Delete API Key"
        message={`Are you sure you want to delete the API key "${deletingKey?.description || "Unnamed Key"}"?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          This action cannot be undone. The API key will be permanently revoked and any applications using it will stop working.
        </Text>
      </ConfirmDialog>
    </OrganizationSettingsTab>
  );
};
